import React, { useState, useEffect, useRef, useCallback } from 'react';
import AdminService from '../services/AdminService';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { markdownToHtml } from '../utils/markdownToHtml';
import {
  FaRobot,
  FaMagic,
  FaPaperPlane,
  FaSave,
  FaTimes,
  FaBook,
  FaClipboardCheck,
  FaSyncAlt,
  FaFileImport,
  FaTrash,
  FaCheckCircle
} from 'react-icons/fa';
import './AdminAIStudio.css';

const MODES = [
  { id: 'draft', label: 'Draft a Lesson', icon: <FaBook /> },
  { id: 'improve', label: 'Improve Existing', icon: <FaMagic /> },
  { id: 'quiz', label: 'Generate Quiz', icon: <FaClipboardCheck /> }
];

const STARTER_PROMPTS = [
  'Write a lesson on how index funds work for a 16-year-old',
  'Give me 5 lesson ideas for a unit on building credit from zero',
  'Outline a lesson comparing Roth IRA vs traditional IRA for teens',
  'Draft a lesson about avoiding common money scams targeting students'
];

const AdminAIStudio = () => {
  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  // Course/unit/lesson data for pickers
  const [courses, setCourses] = useState([]);
  const [lessons, setLessons] = useState([]);

  // Workspace state
  const [mode, setMode] = useState('draft');
  const [draft, setDraft] = useState(null); // { title, content }
  const [draftLessonId, setDraftLessonId] = useState(null); // set when draft came from "improve"
  const [busy, setBusy] = useState(false);

  // Draft-a-lesson form
  const [topic, setTopic] = useState('');
  const [instructions, setInstructions] = useState('');
  const [targetUnitId, setTargetUnitId] = useState('');
  const [xpValue, setXpValue] = useState(50);

  // Improve form
  const [improveLessonId, setImproveLessonId] = useState('');
  const [improveInstructions, setImproveInstructions] = useState('');

  // Quiz form
  const [quizLessonId, setQuizLessonId] = useState('');
  const [numQuestions, setNumQuestions] = useState(5);
  const [quizQuestions, setQuizQuestions] = useState(null);
  const [quizTitle, setQuizTitle] = useState('');

  const [aiModel, setAiModel] = useState(null);
  const chatEndRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [coursesData, lessonsData, statusData] = await Promise.all([
        AdminService.getCoursesAndUnits(),
        AdminService.getLessons(),
        AdminService.aiStatus().catch(() => null)
      ]);
      setCourses(coursesData.courses || []);
      setLessons(lessonsData.lessons || []);
      if (statusData?.status) setAiModel(statusData.status);
    } catch (error) {
      toast.error(error.message || 'Failed to load courses and lessons');
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  // ---------- Chat ----------

  const handleSend = async (presetText = null) => {
    const text = (presetText ?? input).trim();
    if (!text || sending) return;

    soundEffects.playClick();
    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);

    try {
      const data = await AdminService.aiChat(nextMessages);
      setMessages([...nextMessages, { role: 'assistant', content: data.reply }]);
    } catch (error) {
      toast.error(error.message || 'AI request failed');
      // Keep the user's message so they can retry
    } finally {
      setSending(false);
    }
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const looksLikeLesson = (content) => /^# .+/m.test(content || '');

  const loadMessageAsDraft = (content) => {
    soundEffects.playClick();
    const titleMatch = content.match(/^# (.+)$/m);
    setDraft({
      title: titleMatch ? titleMatch[1].trim() : 'Untitled Lesson',
      content
    });
    setDraftLessonId(null);
    setMode('draft');
    toast.success('Draft loaded into the workspace');
  };

  const clearChat = () => {
    soundEffects.playClick();
    setMessages([]);
  };

  // ---------- Draft a lesson ----------

  const handleGenerateLesson = async () => {
    if (!topic.trim()) {
      toast.error('Enter a topic for the lesson');
      return;
    }
    soundEffects.playClick();
    setBusy(true);
    try {
      const data = await AdminService.aiGenerateLesson(
        topic.trim(),
        instructions.trim(),
        targetUnitId ? parseInt(targetUnitId) : null
      );
      setDraft(data.draft);
      setDraftLessonId(null);
      toast.success('Lesson draft generated');
    } catch (error) {
      toast.error(error.message || 'Failed to generate lesson');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveDraftAsLesson = async () => {
    if (!draft) return;
    if (!targetUnitId) {
      toast.error('Choose a unit to save the lesson into');
      return;
    }
    soundEffects.playClick();
    setBusy(true);
    try {
      await AdminService.createLesson({
        unitId: parseInt(targetUnitId),
        title: draft.title,
        content: draft.content,
        xpValue: parseInt(xpValue) || 50,
        isActive: true
      });
      toast.success(`Lesson "${draft.title}" created`);
      setDraft(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save lesson');
    } finally {
      setBusy(false);
    }
  };

  // ---------- Improve existing ----------

  const handleImproveLesson = async () => {
    if (!improveLessonId) {
      toast.error('Choose a lesson to improve');
      return;
    }
    soundEffects.playClick();
    setBusy(true);
    try {
      const data = await AdminService.aiImproveLesson(
        parseInt(improveLessonId),
        improveInstructions.trim()
      );
      setDraft(data.draft);
      setDraftLessonId(data.lessonId);
      toast.success('Improved version ready — review it, then apply');
    } catch (error) {
      toast.error(error.message || 'Failed to improve lesson');
    } finally {
      setBusy(false);
    }
  };

  const handleApplyImprovedDraft = async () => {
    if (!draft || !draftLessonId) return;
    soundEffects.playClick();
    setBusy(true);
    try {
      await AdminService.updateLesson(draftLessonId, {
        title: draft.title,
        content: draft.content
      });
      toast.success('Lesson updated with the improved version');
      setDraft(null);
      setDraftLessonId(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to update lesson');
    } finally {
      setBusy(false);
    }
  };

  // ---------- Quiz ----------

  const handleGenerateQuiz = async () => {
    const fromDraft = !quizLessonId && draft;
    if (!quizLessonId && !fromDraft) {
      toast.error('Choose a lesson (or load a draft) to build a quiz from');
      return;
    }
    soundEffects.playClick();
    setBusy(true);
    try {
      const data = await AdminService.aiGenerateQuiz({
        lessonId: quizLessonId ? parseInt(quizLessonId) : null,
        content: fromDraft ? draft.content : null,
        title: fromDraft ? draft.title : null,
        numQuestions: parseInt(numQuestions) || 5
      });
      setQuizQuestions(data.questions);
      const sourceLesson = quizLessonId
        ? lessons.find((l) => l.id === parseInt(quizLessonId))
        : null;
      setQuizTitle(`${sourceLesson ? sourceLesson.title : draft?.title || 'Lesson'} Quiz`);
      toast.success(`${data.questions.length} questions generated`);
    } catch (error) {
      toast.error(error.message || 'Failed to generate quiz');
    } finally {
      setBusy(false);
    }
  };

  const handleSaveQuiz = async () => {
    if (!quizQuestions || quizQuestions.length === 0) return;
    if (!quizLessonId) {
      toast.error('Choose the lesson this quiz belongs to before saving');
      return;
    }
    soundEffects.playClick();
    setBusy(true);
    try {
      await AdminService.createQuiz({
        lessonId: parseInt(quizLessonId),
        title: quizTitle || 'Lesson Quiz',
        type: 'mixed',
        questions: quizQuestions,
        passingScore: 70,
        xpValue: 25,
        isActive: true
      });
      toast.success('Quiz created');
      setQuizQuestions(null);
    } catch (error) {
      toast.error(error.message || 'Failed to save quiz');
    } finally {
      setBusy(false);
    }
  };

  // ---------- Render helpers ----------

  const renderUnitOptions = () =>
    courses.map((course) =>
      (course.units || []).map((unit) => (
        <option key={unit.id} value={unit.id}>
          {course.title} — {unit.title}
        </option>
      ))
    );

  const renderLessonOptions = () =>
    lessons.map((lesson) => (
      <option key={lesson.id} value={lesson.id}>
        {lesson.course_title} — {lesson.unit_title} — {lesson.title}
      </option>
    ));

  const formatAnswer = (q) => {
    if (q.type === 'multiple_choice') return q.options?.[q.correctAnswer] ?? '';
    if (q.type === 'true_false') return q.correctAnswer ? 'True' : 'False';
    return q.correctAnswer;
  };

  return (
    <div className="ai-studio">
      <div className="ai-studio-header">
        <h2>
          <FaRobot /> AI Content Studio
        </h2>
        <div className="ai-studio-status">
          {aiModel ? (
            aiModel.available ? (
              <span className="ai-status-ok">
                <FaCheckCircle /> {aiModel.model}
              </span>
            ) : (
              <span className="ai-status-bad">API key missing — AI calls will fail</span>
            )
          ) : null}
        </div>
      </div>

      <div className="ai-studio-body">
        {/* Chat pane */}
        <div className="ai-chat-pane">
          <div className="ai-chat-header">
            <span>Chat with the model</span>
            {messages.length > 0 && (
              <button className="ai-chat-clear" onClick={clearChat} title="Clear conversation">
                <FaTrash /> Clear
              </button>
            )}
          </div>

          <div className="ai-chat-messages">
            {messages.length === 0 && !sending && (
              <div className="ai-chat-empty">
                <FaRobot className="ai-chat-empty-icon" />
                <p>
                  Brainstorm ideas, ask for outlines, or request full lesson drafts.
                  Anything that comes back as a lesson can be loaded into the workspace
                  and saved straight into a course.
                </p>
                <div className="ai-starter-prompts">
                  {STARTER_PROMPTS.map((prompt) => (
                    <button key={prompt} onClick={() => handleSend(prompt)}>
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`ai-msg ai-msg-${msg.role}`}>
                <div className="ai-msg-role">{msg.role === 'user' ? 'You' : 'AI'}</div>
                <div
                  className="ai-msg-content"
                  dangerouslySetInnerHTML={{ __html: markdownToHtml(msg.content) }}
                />
                {msg.role === 'assistant' && looksLikeLesson(msg.content) && (
                  <button
                    className="ai-use-draft-btn"
                    onClick={() => loadMessageAsDraft(msg.content)}
                  >
                    <FaFileImport /> Use as lesson draft
                  </button>
                )}
              </div>
            ))}

            {sending && (
              <div className="ai-msg ai-msg-assistant">
                <div className="ai-msg-role">AI</div>
                <div className="ai-msg-content ai-typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="ai-chat-input">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleChatKeyDown}
              placeholder="Ask for a lesson, an outline, ideas… (Enter to send, Shift+Enter for a new line)"
              rows={2}
              disabled={sending}
            />
            <button onClick={() => handleSend()} disabled={sending || !input.trim()}>
              <FaPaperPlane />
            </button>
          </div>
        </div>

        {/* Workspace pane */}
        <div className="ai-workspace-pane">
          <div className="ai-mode-tabs">
            {MODES.map((m) => (
              <button
                key={m.id}
                className={mode === m.id ? 'active' : ''}
                onClick={() => {
                  soundEffects.playClick();
                  setMode(m.id);
                }}
              >
                {m.icon} {m.label}
              </button>
            ))}
          </div>

          {mode === 'draft' && (
            <div className="ai-mode-panel">
              <div className="form-group">
                <label>Topic *</label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="e.g. How dollar-cost averaging beats trying to time the market"
                />
              </div>
              <div className="form-group">
                <label>Extra instructions (optional)</label>
                <textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Tone, examples to include, things to avoid…"
                  rows={2}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Target unit</label>
                  <select value={targetUnitId} onChange={(e) => setTargetUnitId(e.target.value)}>
                    <option value="">Select a unit</option>
                    {renderUnitOptions()}
                  </select>
                </div>
                <div className="form-group ai-xp-group">
                  <label>XP</label>
                  <input
                    type="number"
                    value={xpValue}
                    onChange={(e) => setXpValue(e.target.value)}
                  />
                </div>
              </div>
              <button className="ai-action-btn" onClick={handleGenerateLesson} disabled={busy}>
                {busy ? <FaSyncAlt className="spin" /> : <FaMagic />} Generate Lesson Draft
              </button>
            </div>
          )}

          {mode === 'improve' && (
            <div className="ai-mode-panel">
              <div className="form-group">
                <label>Lesson to improve *</label>
                <select value={improveLessonId} onChange={(e) => setImproveLessonId(e.target.value)}>
                  <option value="">Select a lesson</option>
                  {renderLessonOptions()}
                </select>
              </div>
              <div className="form-group">
                <label>Focus (optional)</label>
                <textarea
                  value={improveInstructions}
                  onChange={(e) => setImproveInstructions(e.target.value)}
                  placeholder="e.g. add a worked example with real numbers, simplify the jargon…"
                  rows={2}
                />
              </div>
              <button className="ai-action-btn" onClick={handleImproveLesson} disabled={busy}>
                {busy ? <FaSyncAlt className="spin" /> : <FaMagic />} Generate Improved Version
              </button>
              <p className="ai-hint">
                Nothing is saved until you review the result and click “Apply to lesson”.
              </p>
            </div>
          )}

          {mode === 'quiz' && (
            <div className="ai-mode-panel">
              <div className="form-row">
                <div className="form-group">
                  <label>Source lesson</label>
                  <select value={quizLessonId} onChange={(e) => setQuizLessonId(e.target.value)}>
                    <option value="">{draft ? 'Current draft (unsaved)' : 'Select a lesson'}</option>
                    {renderLessonOptions()}
                  </select>
                </div>
                <div className="form-group ai-xp-group">
                  <label>Questions</label>
                  <input
                    type="number"
                    min="1"
                    max="15"
                    value={numQuestions}
                    onChange={(e) => setNumQuestions(e.target.value)}
                  />
                </div>
              </div>
              <button className="ai-action-btn" onClick={handleGenerateQuiz} disabled={busy}>
                {busy ? <FaSyncAlt className="spin" /> : <FaClipboardCheck />} Generate Quiz
              </button>

              {quizQuestions && (
                <div className="ai-quiz-preview">
                  <div className="form-group">
                    <label>Quiz title</label>
                    <input
                      type="text"
                      value={quizTitle}
                      onChange={(e) => setQuizTitle(e.target.value)}
                    />
                  </div>
                  {quizQuestions.map((q, i) => (
                    <div key={i} className="ai-quiz-question">
                      <div className="ai-quiz-q">
                        {i + 1}. {q.question}
                        <span className="ai-quiz-type">{q.type.replace('_', ' ')}</span>
                      </div>
                      {q.type === 'multiple_choice' && (
                        <ul>
                          {q.options.map((opt, j) => (
                            <li key={j} className={j === q.correctAnswer ? 'correct' : ''}>
                              {opt}
                            </li>
                          ))}
                        </ul>
                      )}
                      {q.type !== 'multiple_choice' && (
                        <div className="ai-quiz-answer">Answer: {String(formatAnswer(q))}</div>
                      )}
                      {q.explanation && <div className="ai-quiz-explanation">{q.explanation}</div>}
                    </div>
                  ))}
                  <div className="ai-draft-actions">
                    <button className="ai-action-btn" onClick={handleSaveQuiz} disabled={busy}>
                      <FaSave /> Save Quiz
                    </button>
                    <button className="ai-secondary-btn" onClick={() => setQuizQuestions(null)}>
                      <FaTimes /> Discard
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Draft preview + save actions */}
          {draft && mode !== 'quiz' && (
            <div className="ai-draft">
              <div className="ai-draft-header">
                <input
                  type="text"
                  className="ai-draft-title"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                />
                <div className="ai-draft-actions">
                  {draftLessonId ? (
                    <button className="ai-action-btn" onClick={handleApplyImprovedDraft} disabled={busy}>
                      <FaSave /> Apply to Lesson
                    </button>
                  ) : (
                    <button className="ai-action-btn" onClick={handleSaveDraftAsLesson} disabled={busy}>
                      <FaSave /> Save as New Lesson
                    </button>
                  )}
                  <button
                    className="ai-secondary-btn"
                    onClick={() => {
                      soundEffects.playClick();
                      setDraft(null);
                      setDraftLessonId(null);
                    }}
                  >
                    <FaTimes /> Discard
                  </button>
                </div>
              </div>
              <div
                className="ai-draft-preview"
                dangerouslySetInnerHTML={{ __html: markdownToHtml(draft.content) }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAIStudio;
